"use client";

import React from "react";
import { Calculator, Ruler, Gauge, Layers, ArrowRight, SlidersHorizontal, FileText, ShieldCheck, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
export default function CalculationsHero() {
  const {
    lang
  } = useLanguage();
  const copy = {
    badgeSuite: pick(lang, {
      en: "Engineering Calculation Suite",
      zh: "精密工程计算套件",
      es: "Conjunto de cálculo de ingeniería",
      de: "Ingenieur-Berechnungspaket",
      fr: "Suite de calculs d'ingénierie",
      pt: "Pacote de cálculo de engenharia"
    }),
    h1: pick(lang, {
      en: "Precision Wire Mesh & Filtration Calculations",
      zh: "精密金属丝网与工业过滤工程计算指南",
      es: "Cálculos de precisión para mallas metálicas y filtración",
      de: "Präzisionsberechnungen für Drahtgewebe und Filtration",
      fr: "Calculs de précision pour toiles métalliques et filtration",
      pt: "Cálculos de precisão para telas metálicas e filtração"
    }),
    lead: pick(lang, {
      en: "Technical calculation tools and conversion guides engineered for filtration designers, OEM manufacturers, and process engineers. Calculate open area percentages, aperture geometry, differential pressure loss (ΔP), and standard weave tolerances with industrial reliability across 1 to 635 mesh woven cloth and multi-layer sintered metal laminates. Formulated to eliminate underspecification and optimize flow capacity.",
      zh: "为过滤系统设计师、OEM 制造商及化工流体工艺工程师提供标准化计算工具与规格转换指南。支持在线计算开孔率、孔径几何尺寸、初始洁净流体差压（ΔP）及标准公差，参数覆盖 1 至 635 目工业编织网与多层金属烧结滤材。所有模型均符合 ISO 9044 及 ASTM E2016 行业公差标准，确保设备选型与批量投产的高可靠性。",
      es: "Herramientas de cálculo técnico y guías de conversión diseñadas para ingenieros de procesos, diseñadores de filtración y fabricantes OEM. Calcule porcentaje de área abierta, geometría de abertura, pérdida de carga diferencial (ΔP) y tolerancias de tejido con fiabilidad industrial desde 1 hasta 635 mesh y laminados sinterizados multicapa.",
      de: "Technische Berechnungswerkzeuge und Umrechnungsleitfäden für Filtrationsingenieure, OEM-Hersteller und Verfahrenstechniker. Berechnen Sie freie Siebflächen, Maschengeometrien, Differenzdruckverluste (ΔP) und Webtoleranzen von 1 bis 635 Mesh Drahtgewebe und mehrlagigen Sintermetall-Laminaten.",
      fr: "Outils de calcul technique et guides de conversion conçus pour les concepteurs de filtration, les fabricants OEM et les ingénieurs procédés. Calculez les pourcentages de vide, la géométrie des mailles, les pertes de charge différentielles (ΔP) et les tolérances de tissage de 1 à 635 mesh et laminés frittés multicouches.",
      pt: "Ferramentas de cálculo técnico e guias de conversão desenvolvidos para projetistas de filtração, fabricantes OEM e engenheiros de processos. Calcule percentuais de área aberta, geometria de abertura, perda de carga diferencial (ΔP) e tolerâncias de tecelagem de 1 a 635 mesh e laminados sinterizados multicamadas."
    }),
    requestQuoteCta: pick(lang, {
      en: "Request a Quote",
      zh: "索取定制工程报价",
      es: "Solicitar cotización",
      de: "Angebot anfordern",
      fr: "Demander un devis",
      pt: "Solicitar cotação"
    }),
    viewGuidesCta: pick(lang, {
      en: "View Calculation Guides",
      zh: "查看计算公式指南",
      es: "Ver guías de cálculo",
      de: "Berechnungsleitfäden anzeigen",
      fr: "Voir les guides de calcul",
      pt: "Ver guias de cálculo"
    }),
    imgAlt: pick(lang, {
      en: "Pleated stainless steel wire mesh filter element and media geometry",
      zh: "精密折叠不锈钢丝网与多层过滤元件工程结构",
      es: "Elemento filtrante de malla de alambre plisada de acero inoxidable y geometría del medio",
      de: "Plissiertes Edelstahl-Drahtgewebe-Filterelement und Mediengeometrie",
      fr: "Élément filtrant plissé en toile métallique d'acier inoxydable et géométrie du média",
      pt: "Elemento filtrante pregueado em malha de arame de aço inoxidável e geometria do meio"
    }),
    imgBadge: pick(lang, {
      en: "Pleated & Multi-layer Mesh",
      zh: "多层编织与折叠滤网",
      es: "Malla plisada y multicapa",
      de: "Plissiertes & mehrlagiges Gewebe",
      fr: "Toile plissée et multicouche",
      pt: "Malha pregueada e multicamada"
    }),
    benchmarksTitle: pick(lang, {
      en: "Standard Calculation Benchmarks",
      zh: "核心制造与计算基准",
      es: "Pautas estándar de cálculo",
      de: "Standard-Berechnungsrichtwerte",
      fr: "Repères de calcul standard",
      pt: "Parâmetros padrão de cálculo"
    }),
    certLabel: pick(lang, {
      en: "MTR 3.1 option",
      zh: "可按需提供 3.1",
      es: "Opción MTR 3.1",
      de: "Option WAZ 3.1",
      fr: "Option CTM 3.1",
      pt: "Opção MTR 3.1"
    }),
    meshDataLabel: pick(lang, {
      en: "Mesh & Aperture Data",
      zh: "目数与孔径规格表",
      es: "Datos de malla y abertura",
      de: "Maschen- und Öffnungsdaten",
      fr: "Données de mailles et ouvertures",
      pt: "Dados de malha e abertura"
    }),
    viewTables: pick(lang, {
      en: "View Tables",
      zh: "查看表格",
      es: "Ver tablas",
      de: "Tabellen anzeigen",
      fr: "Consulter les tableaux",
      pt: "Ver tabelas"
    }),
    openCalcTool: pick(lang, {
      en: "Open Calculation Tool",
      zh: "打开计算工具",
      es: "Abrir herramienta de cálculo",
      de: "Berechnungstool öffnen",
      fr: "Ouvrir l'outil de calcul",
      pt: "Abrir ferramenta de cálculo"
    })
  };
  const specHighlights = [{
    label: pick(lang, {
      en: "Standard Mesh Counts",
      zh: "标准目数范围",
      es: "Rango estándar de mallas",
      de: "Standard-Maschenzahlen",
      fr: "Nombre de mailles standard",
      pt: "Faixa padrão de malhas"
    }),
    val: pick(lang, {
      en: "1 – 635 Mesh",
      zh: "1 – 635 目",
      es: "1 – 635 mesh",
      de: "1 – 635 Mesh",
      fr: "1 – 635 mesh",
      pt: "1 – 635 mesh"
    })
  }, {
    label: pick(lang, {
      en: "Micron Retention",
      zh: "微米截留范围",
      es: "Retención en micras",
      de: "Mikron-Rückhaltung",
      fr: "Rétention micrométrique",
      pt: "Retenção em mícrons"
    }),
    val: "1 µm – 25 mm"
  }, {
    label: pick(lang, {
      en: "Engineering Standards",
      zh: "执行工程标准",
      es: "Normas de ingeniería",
      de: "Ingenieurnormen",
      fr: "Normes d'ingénierie",
      pt: "Normas de engenharia"
    }),
    val: "ASTM E2016 / ISO 9044"
  }, {
    label: pick(lang, {
      en: "Common Alloys",
      zh: "常用合金材质",
      es: "Aleaciones comunes",
      de: "Gängige Legierungen",
      fr: "Alliages courants",
      pt: "Ligas comuns"
    }),
    val: "AISI 304 / 304L / 316 / 316L"
  }];
  const calculationTools = [{
    title: pick(lang, {
      en: "Open Area & Aperture",
      zh: "开孔率与孔径计算",
      es: "Área abierta y abertura",
      de: "Freie Siebfläche & Maschenweite",
      fr: "Surface ouverte et ouverture",
      pt: "Área aberta e abertura"
    }),
    description: pick(lang, {
      en: "Calculate percentage open area, aperture dimension (w), and wire diameter (d) relationships.",
      zh: "计算有效流通开孔率百分比、网孔净尺寸 (w) 与丝径 (d) 的几何关系。",
      es: "Calcule el porcentaje de área abierta, la abertura (w) y el diámetro del alambre (d).",
      de: "Berechnen Sie freie Siebflächen (Ao), Maschenweiten (w) und Drahtdurchmesser (d).",
      fr: "Calculez le pourcentage de vide, l'ouverture utile (w) et le diamètre du fil (d).",
      pt: "Calcule percentual de área aberta, dimensão de abertura (w) e diâmetro do arame (d)."
    }),
    icon: Calculator,
    link: "/CalculationGuidesPage",
    badge: "ISO 9044 Formula",
    metric: "Ao = (w / (w + d))² × 100%"
  }, {
    title: pick(lang, {
      en: "Mesh Count to Micron",
      zh: "目数与微米换算",
      es: "Conversión de mesh a micras",
      de: "Maschenzahl zu Mikron",
      fr: "Nombre de mailles en microns",
      pt: "Conversão de mesh para mícrons"
    }),
    description: pick(lang, {
      en: "Convert mesh count per inch to precise nominal aperture micron ratings across weave styles.",
      zh: "将每线性英寸目数换算为标准公称孔径微米等级与颗粒截留极限。",
      es: "Convierta número de mallas por pulgada a valores nominales en micras para diversos tejidos.",
      de: "Konvertieren Sie die Maschenzahl pro Zoll in präzise nominale Mikron-Abscheidegrade.",
      fr: "Convertissez le nombre de mailles par pouce en seuil nominal en microns selon le tissage.",
      pt: "Converta contagem de malha por polegada em classificações nominais em mícrons."
    }),
    icon: Ruler,
    link: "/MeshCountAndAperturePage",
    badge: "ASTM E2016",
    metric: "1 Mesh = 25.4mm / (w + d)"
  }, {
    title: pick(lang, {
      en: "Pressure Drop & Flow",
      zh: "压降与流阻模型",
      es: "Pérdida de carga y flujo",
      de: "Druckverlust & Durchfluss",
      fr: "Perte de charge et débit",
      pt: "Queda de pressão e vazão"
    }),
    description: pick(lang, {
      en: "Estimate differential pressure loss (ΔP) and liquid/gas flow velocity through sintered & woven mesh.",
      zh: "估算穿流编织网与烧结复合板的洁净流体压差损耗 (ΔP) 及迎面流速。",
      es: "Estime la pérdida de carga diferencial (ΔP) y la velocidad de flujo a través de medios tejidos y sinterizados.",
      de: "Bestimmen Sie Druckverlust (ΔP) und Durchströmungsgeschwindigkeit durch Draht- und Sintergewebe.",
      fr: "Estimez la perte de charge différentielle (ΔP) et la vitesse d'écoulement à travers toiles et médias frittés.",
      pt: "Estime a perda de carga diferencial (ΔP) e a velocidade de vazão através de telas tecidas e sinterizadas."
    }),
    icon: Gauge,
    link: "/DifferentialPressureDeltaPModelsPage",
    badge: "Fluid Dynamics",
    metric: "ΔP = ξ · ½ · ρ · v²"
  }, {
    title: pick(lang, {
      en: "Layer & Porosity Index",
      zh: "烧结网层叠与孔隙率",
      es: "Capas e índice de porosidad",
      de: "Lagenaufbau & Porosität",
      fr: "Couches et indice de porosité",
      pt: "Camadas e índice de porosidade"
    }),
    description: pick(lang, {
      en: "Evaluate multi-layer sintered mesh structural density, permeability, and dirt holding capacity.",
      zh: "评估多层扩散烧结金属网结构孔隙度、流体渗透率与机械纳污容量。",
      es: "Evalúe densidad estructural, permeabilidad y capacidad de retención de suciedad en mallas sinterizadas multicapa.",
      de: "Bewerten Sie strukturelle Dichte, Permeabilität und Schmutzaufnahmekapazität mehrlagiger Sintergewebe.",
      fr: "Évaluez la densité structurale, la perméabilité et la capacité de rétention de particules des toiles frittées.",
      pt: "Avalie densidade estrutural, permeabilidade e capacidade de retenção de particulados em telas sinterizadas multicamadas."
    }),
    icon: Layers,
    link: "/TechnicalChartsPage",
    badge: "Sintered Mesh",
    metric: "Porosity = (1 - ρ_mesh/ρ_solid) × 100%"
  }];
  return <section id="calculationshero" data-nav="light" className="bg-background text-foreground mf-hero border-b border-border">
      <div className="mf-container">
        <Reveal>
          {/* Top 7/5 Grid Hero Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-8">
            {/* Left Column (7 cols): Badges, H1, Lead, 2 CTAs */}
            <div className="lg:col-span-7 space-y-4">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge variant="outline" className="rounded-md px-3 py-1 font-mono mf-caption bg-card border-border text-foreground gap-1.5">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  {copy.badgeSuite}
                </Badge>
                <Badge variant="outline" className="rounded-md px-3 py-1 font-mono mf-caption bg-muted border-border text-foreground gap-1.5">
                  <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                  ASTM E2016 / ISO 9044
                </Badge>
              </div>

              <h1 className="mf-h1 text-foreground font-default text-balance">
                {copy.h1}
              </h1>

              <p className="mf-lead text-muted-foreground max-w-2xl mt-4">
                {copy.lead}
              </p>

              {/* Two CTAs */}
              <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                <Button asChild size="lg" className="px-6 font-semibold">
                  <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                    <span>{pick(lang, COMMON.requestQuote)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="px-6 font-semibold">
                  <Link to="/CalculationGuidesPage" className="inline-flex items-center gap-2">
                    <FileText className="size-4 text-primary" aria-hidden="true" />
                    <span>{copy.viewGuidesCta}</span>
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Column (5 cols): Visual & Spec Panel */}
            <div className="lg:col-span-5 h-full">
              <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
                <div className="mf-card-media mf-ratio-hero w-full">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pleated-sta-f9bdad.png" alt={copy.imgAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                    <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{copy.imgBadge}</span>
                  </div>
                </div>

                {/* Card Content: Single footer row only */}
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mf-caption">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                      <span>{copy.meshDataLabel}</span>
                    </div>
                    <Link to="/MeshCountAndAperturePage" className="font-medium text-primary hover:underline flex items-center gap-1">
                      <span>{copy.viewTables}</span>
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Spec Strip below 12-col grid */}
          <div className="mf-spec-strip mb-8">
            {specHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
                <span className="mf-spec-strip__label">
                  {spec.label}
                </span>
                <span className="mf-spec-strip__value">
                  {spec.val}
                </span>
              </div>)}
          </div>

          {/* Quick Calculator Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {calculationTools.map((tool, i) => {
            const IconComponent = tool.icon;
            return <div key={i} data-index={i} className="bg-card text-card-foreground border border-border rounded-lg p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                        <IconComponent className="size-5" aria-hidden="true" />
                      </div>
                      <Badge variant="outline" className="mf-caption font-mono font-normal">
                        {tool.badge}
                      </Badge>
                    </div>

                    <h2 className="text-base font-semibold text-foreground mb-1.5">
                      {tool.title}
                    </h2>

                    <p className="mf-caption text-muted-foreground leading-relaxed mb-4">
                      {tool.description}
                    </p>
                  </div>

                  <div>
                    <div className="bg-muted/60 font-mono mf-caption text-muted-foreground p-2 rounded border border-border/50 mb-3 truncate">
                      {tool.metric}
                    </div>

                    <Link to={tool.link} className="inline-flex items-center mf-caption font-medium text-primary hover:underline gap-1">
                      <span>{copy.openCalcTool}</span>
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </Link>
                  </div>
                </div>;
          })}
          </div>
        </Reveal>
      </div>
    </section>;
}